// src/services/api.js
import { authFetch } from '../utils/auth-interceptor';
import { applyUserRoleOverride } from '../utils/user-role-override';

const DIRECT_API_URL = (process.env.VUE_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');

/**
 * Логин (получение JWT) – form-urlencoded
 * @param {string} username - Имя пользователя
 * @param {string} password - Пароль
 * @returns {Promise<Object>} - Токены доступа
 */
export const login = async (username, password) => {
  const response = await authFetch('/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ username, password }).toString()
  });

  // Сохраняем токены в localStorage
  localStorage.setItem('token', response.access_token);
  localStorage.setItem('refresh_token', response.refresh_token);

  return response;
};

/**
 * Получить данные текущего пользователя
 * @returns {Promise<Object>} - Данные пользователя
 */
export const fetchUserData = async () => {
  const userData = await authFetch('/admin/users/me');
  return applyUserRoleOverride(userData);
};

/**
 * Получает список всех пользователей
 * @param {boolean} includeInactive - Включить деактивированных пользователей
 * @returns {Promise<Array>} - Массив пользователей
 */
export const getUsersApi = async (includeInactive = true, category = null) => {
  try {
    const response = await authFetch('/admin/users', {
      params: {
        include_inactive: includeInactive ? 'true' : undefined,
        category: category || undefined
      }
    });

    if (!Array.isArray(response)) {
      console.warn('Unexpected API response format. Expected array, got:', response);
      return [];
    }

    return response;
  } catch (error) {
    console.error('Failed to fetch users:', error);
    
    // Можно добавить обработку специфичных ошибок
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your internet connection.');
    }
    
    throw new Error(`Failed to load users: ${error.message}`);
  }
};

export const fetchPublicUsersApi = async (category = null) => {
  try {
    const response = await authFetch('/users', {
      params: category ? { category } : undefined
    });

    if (!Array.isArray(response)) {
      console.warn('Unexpected public users response format. Expected array, got:', response);
      return [];
    }

    return response;
  } catch (error) {
    console.error('Failed to fetch public users:', error);
    throw new Error(`Failed to load public users: ${error.message}`);
  }
};

export const fetchUserCategoriesApi = async () => {
  const response = await authFetch('/users/categories');
  return Array.isArray(response) ? response : [];
};

export const getAdminUserCategoriesApi = async () => {
  const response = await authFetch('/admin/users/categories');
  return Array.isArray(response) ? response : [];
};

export const createAdminUserCategoryApi = (name) => {
  return authFetch('/admin/users/categories', {
    method: 'POST',
    body: { name }
  });
};

export const deleteAdminUserCategoryApi = (categoryId) => {
  return authFetch(`/admin/users/categories/${categoryId}`, {
    method: 'DELETE'
  });
};

/**
 * Получить список файлов с пагинацией, поиском и сортировкой
 * @param {number} skip - Количество пропускаемых элементов
 * @param {number} limit - Максимальное количество элементов
 * @param {string} search - Строка поиска
 * @param {string} sort_by - Поле для сортировки
 * @returns {Promise<Object>} - Список файлов с пагинацией
 */
export const fetchFiles = (skip, limit, search, sort_by) => {
  return authFetch('/admin/files', {
    params: { skip, limit, search, sort_by }
  });
};

export const fetchMyFiles = (skip, limit, search, sort_by) => {
  return authFetch('/admin/files/my', {
    params: { skip, limit, search, sort_by }
  });
};

/**
 * Получить заявки
 * @param {number} skip - Количество пропускаемых элементов
 * @param {number} limit - Максимальное количество элементов
 * @param {string} search - Строка поиска
 * @returns {Promise<Object>} - Список заявок с пагинацией
 */
export const fetchApplications = (skip, limit, search) => {
  return authFetch('/admin/applications', {
    params: { skip, limit, search }
  });
};

/**
 * Загрузить файл
 * @param {File} file - Загружаемый файл
 * @returns {Promise<Object>} - Информация о загруженном файле
 */
export const uploadFile = file => {
  const formData = new FormData();
  formData.append('file', file);
  
  return authFetch('/admin/files', { 
    method: 'POST', 
    body: formData
  });
};

/**
 * Загрузить файл с отслеживанием прогресса
 * @param {File} file - Загружаемый файл
 * @param {Function} onProgress - Callback для отслеживания прогресса (progress) => {}
 * @param {Function} onError - Callback для обработки ошибок (error) => {}
 * @returns {Promise<Object>} - Информация о загруженном файле
 */
export const uploadFileWithProgress = (file, onProgress = () => {}, onError = () => {}) => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const xhr = new XMLHttpRequest();
    const token = localStorage.getItem('token');
    
    xhr.open('POST', `${DIRECT_API_URL}/admin/files`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    // Отслеживание прогресса загрузки
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });
    
    // Обработка завершения
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (e) {
          reject(new Error('Ошибка парсинга ответа сервера'));
        }
      } else {
        const error = new Error(`HTTP Error: ${xhr.status}`);
        onError(error);
        reject(error);
      }
    });
    
    // Обработка ошибок
    xhr.addEventListener('error', () => {
      const error = new Error('Ошибка сети при загрузке файла');
      onError(error);
      reject(error);
    });
    
    // Обработка отмены
    xhr.addEventListener('abort', () => {
      const error = new Error('Загрузка файла отменена');
      onError(error);
      reject(error);
    });
    
    xhr.send(formData);
  });
};

/**
 * Загрузить большой файл частями (chunked upload)
 * @param {File} file - Загружаемый файл
 * @param {Function} onProgress - Callback для отслеживания прогресса
 * @param {Function} onError - Callback для обработки ошибок
 * @param {number} chunkSize - Размер части в байтах (по умолчанию 1MB)
 * @returns {Promise<Object>} - Информация о загруженном файле
 */
export const uploadFileChunked = async (file, onProgress = () => {}, onError = () => {}, chunkSize = 1024 * 1024) => {
  const fileId = generateUUID();
  const totalChunks = Math.ceil(file.size / chunkSize);
  let uploadedChunks = 0;
  
  try {
    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      
      const formData = new FormData();
      formData.append('file', chunk);
      formData.append('chunk_number', i.toString());
      formData.append('total_chunks', totalChunks.toString());
      formData.append('file_id', fileId);
      formData.append('filename', file.name);
      
      const response = await authFetch('/admin/files/chunked', {
        method: 'POST',
        body: formData
      });
      
      uploadedChunks++;
      const progress = Math.round((uploadedChunks / totalChunks) * 100);
      onProgress(progress);
      
      if (response.status === 'completed') {
        return response;
      }
    }
  } catch (error) {
    onError(error);
    throw error;
  }
};

/**
 * Генерировать UUID для файлов
 * @returns {string} - UUID строка
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Альтернативный uploadFileApi (как было)
export const uploadFileApi = uploadFileWithProgress;

/**
 * Создать пользователя (superuser only)
 * @param {Object} userData - Данные пользователя
 * @returns {Promise<Object>} - Созданный пользователь
 */
export const createUser = userData => {
  return authFetch('/admin/users', {
    method: 'POST',
    body: userData
  });
};

/**
 * Обновить пользователя (superuser only)
 * @param {string} userId - UUID пользователя
 * @param {Object} userData - Данные пользователя
 * @returns {Promise<Object>} - Обновленный пользователь
 */
export const updateUser = (userId, userData) => {
  return authFetch(`/admin/users/${userId}`, {
    method: 'PUT',
    body: userData
  });
};

// Альтернативная функция для обновления пользователя
export const updateUserApi = async (userId, userData) => {
  return await updateUser(userId, userData);
};

/**
 * Удалить пользователя (superuser only)
 * @param {string} userId - UUID пользователя
 * @returns {Promise<Object>} - Результат удаления
 */
export const deleteUserApi = (userId) => {
  return authFetch(`/admin/users/${userId}`, {
    method: 'DELETE'
  });
};

/**
 * Деактивировать пользователя (superuser only)
 * @param {string} userId - UUID пользователя
 * @returns {Promise<Object>} - Обновленный пользователь
 */
export const deactivateUserApi = (userId) => {
  return authFetch(`/admin/users/${userId}/deactivate`, {
    method: 'POST'
  });
};

/**
 * Активировать пользователя (superuser only)
 * @param {string} userId - UUID пользователя
 * @returns {Promise<Object>} - Обновленный пользователь
 */
export const activateUserApi = (userId) => {
  return authFetch(`/admin/users/${userId}/activate`, {
    method: 'POST'
  });
};

/**
 * Получить список проектов
 * @returns {Promise<Array>} - Массив проектов
 */
export const fetchProjects = () => authFetch('/admin/projects');
export const fetchMyProjects = () => authFetch('/admin/projects/my');

/**
 * Получить проект по ID
 * @param {string|number} projectID - ID проекта
 * @returns {Promise<Object>} - Проект
 */
export const fetchProject = (projectID) => authFetch(`/admin/projects/${projectID}`);

/**
 * Получить простую ленту файлов
 * @returns {Promise<Array>} - Массив файлов
 */
export const fetchFilesApi = () => authFetch('/admin/files');

/**
 * Удалить файл
 * @param {string} uuid - UUID файла
 * @returns {Promise<Object>} - Результат удаления
 */
export const deleteFileApi = (uuid) => {
  return authFetch(`/admin/files/${uuid}`, { method: 'DELETE' });
};

/**
 * Обновить проект
 * @param {string|number} projectId - ID проекта
 * @param {Object} data - Данные проекта
 * @returns {Promise<Object>} - Обновленный проект
 */
export const updateProjectApi = async (projectId, data) => {
  // Если в данных есть дата, убедимся что она в формате ISO с временем
  if (data.date) {
    const dateObj = new Date(data.date);
    data.date = dateObj.toISOString().split('T')[0] + 'T00:00:00';
  }
  
  return await authFetch(`/admin/projects/${projectId}`, {
    method: 'PUT',
    body: data
  });
};

/**
 * Удалить проект
 * @param {string|number} projectId - ID проекта
 * @returns {Promise<Object>} - Результат удаления
 */
export const deleteProjectApi = async (projectId) => {
  return await authFetch(`/admin/projects/${projectId}`, { method: 'DELETE' });
};

/**
 * Создать новый проект
 * @param {Object} projectData - Данные проекта
 * @returns {Promise<Object>} - Созданный проект
 */
export const addProjectApi = async (projectData) => {
  // Убедимся, что дата в формате ISO с временем
  if (projectData.date) {
    const dateObj = new Date(projectData.date);
    projectData.date = dateObj.toISOString().split('T')[0] + 'T00:00:00';
  }
  
  return await authFetch('/admin/projects', {
    method: 'POST',
    body: projectData
  });
};

/**
 * BLOG V2 (Проекты V2)
 * Статьи и блоки
 */

// Статьи
export const fetchBlogArticles = () => authFetch('/admin/blog/articles');
export const fetchMyBlogArticles = () => authFetch('/admin/blog/articles/my');

export const createBlogArticle = (data) => {
  // data: { slug, title, description?, visible? }
  return authFetch('/admin/blog/articles', {
    method: 'POST',
    body: data
  });
};

export const fetchBlogArticle = (articleId) => authFetch(`/admin/blog/articles/${articleId}`);

export const updateBlogArticle = (articleId, data) => {
  return authFetch(`/admin/blog/articles/${articleId}`, {
    method: 'PUT',
    body: data
  });
};

export const deleteBlogArticle = (articleId) => authFetch(`/admin/blog/articles/${articleId}`, { method: 'DELETE' });

// Блоки
export const createBlogBlock = (articleId, blockData) => {
  // blockData: { type, payload, position? }
  return authFetch(`/admin/blog/articles/${articleId}/blocks`, {
    method: 'POST',
    body: blockData
  });
};

export const updateBlogBlock = (articleId, blockId, blockData) => {
  return authFetch(`/admin/blog/articles/${articleId}/blocks/${blockId}`, {
    method: 'PUT',
    body: blockData
  });
};

export const deleteBlogBlock = (articleId, blockId) => authFetch(`/admin/blog/articles/${articleId}/blocks/${blockId}`, { method: 'DELETE' });

/**
 * Обновить позиции блоков статьи
 * @param {number} articleId - ID статьи
 * @param {Array} blocks - Массив блоков с обновленными позициями
 * @returns {Promise<Object>} - Результат обновления
 */
export const updateBlogBlockPositions = async (articleId, blocks) => {
  const updates = blocks.map(block => ({
    id: parseInt(block.id), // Преобразуем ID в целое число
    position: parseInt(block.position) // Преобразуем позицию в целое число
  }));
  
  return authFetch(`/admin/blog/articles/${articleId}/blocks/positions`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blocks: updates })
  });
};

/**
 * VIDEO
 */
export const uploadVideo = async (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const token = localStorage.getItem('token');
    
    xhr.open('POST', `${DIRECT_API_URL}/videos/upload`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (e) {
          reject(new Error('Ошибка парсинга ответа сервера'));
        }
      } else {
        reject(new Error(`HTTP Error: ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => reject(new Error('Ошибка сети при загрузке видео')));
    
    xhr.send(formData);
  });
};

export const fetchVideos = (skip = 0, limit = 10) => {
  return authFetch('/videos/list', {
    params: { skip, limit }
  });
};

export const fetchMyVideos = (skip = 0, limit = 10) => {
  return authFetch('/videos/my', {
    params: { skip, limit }
  });
};

export const deleteVideoApi = (videoId) => {
  return authFetch(`/videos/${videoId}`, { method: 'DELETE' });
};

/**
 * Создать приглашение
 * @param {string} baseUrl - Базовый URL для формирования ссылки приглашения
 * @returns {Promise<Object>} - Данные приглашения с ссылкой
 */
export const createInvitationApi = () => {
  return authFetch('/admin/invitations', {
    method: 'POST',
    params: {}
  });
};

/**
 * SYSTEM SETTINGS (superuser only)
 */
export const fetchSystemSettings = () => authFetch('/admin/system-settings');

export const updateSystemSettings = (data) => {
  return authFetch('/admin/system-settings', {
    method: 'PUT',
    body: data
  });
};

/**
 * Получить спецификации файлов (рекомендации для загрузки)
 * @returns {Promise<Object>} - Спецификации файлов
 */
export const getFileSpecifications = () => authFetch('/admin/file-specs');

/**
 * BACKSTAGE GALLERY API
 * Управление галереями backstage изображений
 */

/**
 * Получить изображения backstage для указанного контекста
 * @param {Object} params - Параметры запроса
 * @param {string} params.context - Контекст (main, user, project)
 * @param {string} params.context_id - ID контекста (null для main)
 * @returns {Promise<Array>} - Массив изображений backstage
 */
export const getBackstageImages = ({ context, context_id }) => {
  const params = new URLSearchParams();
  if (context) params.append('context', context);
  if (context_id) params.append('context_id', context_id);

  return authFetch(`/admin/backstage-extended?${params.toString()}`);
};

/**
 * Создать изображение backstage
 * @param {Object} data - Данные изображения
 * @param {string} data.file_id - ID файла
 * @param {string} data.context - Контекст
 * @param {string} data.context_id - ID контекста
 * @param {number} data.position - Позиция
 * @returns {Promise<Object>} - Созданное изображение backstage
 */
export const createBackstageImage = (data) => {
  return authFetch('/admin/backstage-extended', {
    method: 'POST',
    body: data
  });
};

/**
 * Обновить изображение backstage
 * @param {string} id - ID изображения backstage
 * @param {Object} data - Данные для обновления
 * @returns {Promise<Object>} - Обновленное изображение backstage
 */
export const updateBackstageImage = (id, data) => {
  return authFetch(`/admin/backstage-extended/${id}`, {
    method: 'PUT',
    body: data
  });
};

/**
 * Удалить изображение backstage
 * @param {string} id - ID изображения backstage
 * @returns {Promise<Object>} - Результат удаления
 */
export const deleteBackstageImage = (id) => {
  return authFetch(`/admin/backstage-extended/${id}`, {
    method: 'DELETE'
  });
};

/**
 * COMMERCIAL API
 */
export const fetchCommercials = () => authFetch('/admin/commercial');

export const createCommercial = (data) => {
  return authFetch('/admin/commercial', {
    method: 'POST',
    body: data
  });
};

export const fetchCommercial = (id) => authFetch(`/admin/commercial/${id}`);

export const updateCommercial = (id, data) => {
  return authFetch(`/admin/commercial/${id}`, {
    method: 'PUT',
    body: data
  });
};

export const deleteCommercial = (id) => authFetch(`/admin/commercial/${id}`, { method: 'DELETE' });

export const addCommercialBlock = (proposalId, data) => {
  return authFetch(`/admin/commercial/${proposalId}/blocks`, {
    method: 'POST',
    body: data
  });
};

export const updateCommercialBlock = (proposalId, blockId, data) => {
  return authFetch(`/admin/commercial/${proposalId}/blocks/${blockId}`, {
    method: 'PUT',
    body: data
  });
};

export const deleteCommercialBlock = (proposalId, blockId) => {
  return authFetch(`/admin/commercial/${proposalId}/blocks/${blockId}`, { method: 'DELETE' });
};
